/**
 * Social media sharing utilities
 */

interface ShareData {
  title: string;
  text: string;
  url?: string;
}

// Share quiz results
export const shareQuizResult = async (score: number, total: number, lessonTitle: string) => {
  const percentage = Math.round((score / total) * 100);
  const emoji = percentage >= 90 ? '🎉' : percentage >= 80 ? '🌟' : percentage >= 70 ? '✨' : '📚';
  
  const shareData: ShareData = {
    title: 'Shikhun Italian - My Quiz Result',
    text: `${emoji} I just scored ${score}/${total} (${percentage}%) on "${lessonTitle}"! Learning Italian with Shikhun Italian! 🇮🇹`,
    url: window.location.origin
  };

  return shareContent(shareData);
};

// Share learning progress
export const shareLearningProgress = async (streak: number, xp: number, lessonsCompleted: number) => {
  const shareData: ShareData = {
    title: 'Shikhun Italian - My Learning Progress',
    text: `🔥 ${streak} day streak | ⭐ ${xp} XP | 📚 ${lessonsCompleted} lessons completed! Join me in learning Italian! 🇮🇹`,
    url: window.location.origin
  };

  return shareContent(shareData);
};

// Share lesson completion
export const shareLessonCompletion = async (lessonTitle: string) => {
  const shareData: ShareData = {
    title: 'Shikhun Italian - Lesson Completed',
    text: `✅ Just completed "${lessonTitle}" on Shikhun Italian! Learning Italian one lesson at a time! 🇮🇹`,
    url: window.location.origin
  };

  return shareContent(shareData);
};

// Generic share function
const shareContent = async (shareData: ShareData): Promise<boolean> => {
  // Check if Web Share API is available
  if (navigator.share) {
    try {
      await navigator.share(shareData);
      return true;
    } catch (error) {
      if ((error as Error).name !== 'AbortError') {
        console.warn('Error sharing:', error);
      }
      // User cancelled, fallback to copy
      return fallbackShare(shareData);
    }
  } else {
    // Web Share API not available, use fallback
    return fallbackShare(shareData);
  }
};

// Fallback share - show social media options for desktop
const fallbackShare = (shareData: ShareData): boolean => {
  const text = shareData.text;
  const url = shareData.url || window.location.origin;
  
  // Create modal with social media buttons
  const modal = document.createElement('div');
  modal.className = 'share-modal-overlay';
  modal.innerHTML = `
    <div class="share-modal-content">
      <button class="share-modal-close">×</button>
      <h3>Share Your Achievement</h3>
      <p class="share-text-preview">${text}</p>
      <p class="share-instruction">👆 Copy the text above and paste it when you share!</p>
      <div class="share-buttons">
        <a href="https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}" 
           target="_blank" 
           class="share-btn facebook"
           aria-label="Share on Facebook">
          <span class="share-icon">📘</span>
          <span>Facebook</span>
        </a>
        <a href="https://twitter.com/intent/tweet?text=${encodeURIComponent(text + ' ' + url)}" 
           target="_blank" 
           class="share-btn twitter"
           aria-label="Share on Twitter">
          <span class="share-icon">🐦</span>
          <span>Twitter</span>
        </a>
        <a href="https://wa.me/?text=${encodeURIComponent(text + ' ' + url)}" 
           target="_blank" 
           class="share-btn whatsapp"
           aria-label="Share on WhatsApp">
          <span class="share-icon">💬</span>
          <span>WhatsApp</span>
        </a>
        <a href="https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}" 
           target="_blank" 
           class="share-btn telegram"
           aria-label="Share on Telegram">
          <span class="share-icon">✈️</span>
          <span>Telegram</span>
        </a>
        <a href="https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}" 
           target="_blank" 
           class="share-btn linkedin"
           aria-label="Share on LinkedIn">
          <span class="share-icon">💼</span>
          <span>LinkedIn</span>
        </a>
        <button class="share-btn copy" data-text="${text}" aria-label="Copy text to clipboard">
          <span class="share-icon">📋</span>
          <span>Copy Text</span>
        </button>
      </div>
    </div>
  `;
  
  document.body.appendChild(modal);
  
  // Close modal handlers
  const closeBtn = modal.querySelector('.share-modal-close');
  const copyBtn = modal.querySelector('.share-btn.copy');
  const textPreview = modal.querySelector('.share-text-preview');
  
  const closeModal = () => {
    modal.remove();
  };
  
  closeBtn?.addEventListener('click', closeModal);
  modal.addEventListener('click', (e) => {
    if (e.target === modal) closeModal();
  });
  
  // Auto-copy when clicking text preview
  textPreview?.addEventListener('click', () => {
    const textToCopy = text;
    navigator.clipboard.writeText(textToCopy).then(() => {
      const instruction = modal.querySelector('.share-instruction');
      if (instruction) {
        instruction.textContent = '✅ Text copied! Now click a platform to share.';
      }
    });
  });
  
  // Copy button handler
  copyBtn?.addEventListener('click', () => {
    const textToCopy = copyBtn.getAttribute('data-text') || '';
    navigator.clipboard.writeText(textToCopy).then(() => {
      const copyBtnElement = copyBtn as HTMLElement;
      copyBtnElement.innerHTML = '<span class="share-icon">✅</span><span>Copied!</span>';
      setTimeout(() => {
        copyBtnElement.innerHTML = '<span class="share-icon">📋</span><span>Copy Text</span>';
      }, 2000);
    });
  });
  
  return true;
};

// Direct social media links (alternative option)
export const getSocialShareLinks = (text: string, url: string) => {
  const encodedText = encodeURIComponent(text);
  const encodedUrl = encodeURIComponent(url);
  
  return {
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${encodedText}`,
    twitter: `https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`,
    whatsapp: `https://wa.me/?text=${encodedText}%20${encodedUrl}`,
    telegram: `https://t.me/share/url?url=${encodedUrl}&text=${encodedText}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`
  };
};

